/**
 * 📝 LOGGER SERVICE
 * -----------------
 * Centralized logging facade.
 * Wraps console to allow filtering, remote logging, and stripping in production.
 *
 * DEV ONLY: prepends `[X.Xs]` elapsed since module load to every line for
 * boot-time diagnosis. Zero runtime cost in production (IS_DEV gated).
 *
 * Extracted 2026-09-03 from three byte-identical copies (legacy, teacher,
 * backoffice core/services/logger.ts) — the only triple copy no gate watched.
 * Browser-under-Vite only: `import.meta.env` is replaced at build time, so
 * this package is not consumable from plain Node or Deno EFs.
 */

const IS_DEV = import.meta.env.DEV;
const BOOT_START = typeof performance !== 'undefined' ? performance.now() : 0;

function t(): string {
    if (!IS_DEV) return '';
    const elapsedMs = performance.now() - BOOT_START;
    const secs = (elapsedMs / 1000).toFixed(2);
    return `[${secs}s] `;
}

export const Logger = {
    info(message: string, ...args: unknown[]) {
        if (IS_DEV) {
            console.log(`${t()}ℹ️ [Info] ${message}`, ...args);
        }
    },

    warn(message: string, ...args: unknown[]) {
        console.warn(`${t()}⚠️ [Warn] ${message}`, ...args);
    },

    error(message: string, ...args: unknown[]) {
        console.error(`${t()}🚨 [Error] ${message}`, ...args);
    },

    debug(message: string, ...args: unknown[]) {
        if (IS_DEV) {
            console.debug(`${t()}🐞 [Debug] ${message}`, ...args);
        }
    },

    // Lifecycle/System logs that might be useful even in prod (optional)
    system(message: string, ...args: unknown[]) {
        if (IS_DEV) {
            console.log(`${t()}⚙️ [System] ${message}`, ...args);
        }
    }
};

export default Logger;
